/**
 * CLE-2811 — the documents chosen in the "New contract model" dialog, and what each one is doing: queued
 * until "Start modeling", then uploading, then done or failed. Pure, so each rule is tested without a DOM.
 */

import { isAllowedUpload } from "./uploadDocument";

/** How many documents the dialog uploads at once. */
export const NEW_MODEL_UPLOAD_CONCURRENCY = 3;

export type PickedFileStatus = "queued" | "uploading" | "done" | "failed";

export interface IPickedFile {
  /** Two files may share a name; this is what tells them apart. */
  key: string;
  file: File;
  status: PickedFileStatus;
  /** Why the last attempt failed, in words. */
  error: string | null;
}

export interface IPickedFilesState {
  files: IPickedFile[];
  /** The files the last add refused, by name: not a type the AI can read. */
  rejected: string[];
  nextId: number;
}

export const INITIAL_PICKED_FILES: IPickedFilesState = {
  files: [],
  rejected: [],
  nextId: 1,
};

export type PickedFilesAction =
  | { type: "add"; files: File[] }
  | { type: "remove"; key: string }
  | { type: "uploading"; key: string }
  | { type: "done"; key: string }
  | { type: "failed"; key: string; error: string }
  | { type: "reset" };

/** A file can be taken off the list until it is on the model: queued, or failed. */
export const isRemovable = (file: IPickedFile): boolean =>
  file.status === "queued" || file.status === "failed";

/** The files still to go up when the dialog uploads: everything not done and not already going. */
export const pendingFiles = (files: IPickedFile[]): IPickedFile[] =>
  files.filter((f) => f.status === "queued" || f.status === "failed");

const setStatus = (
  state: IPickedFilesState,
  key: string,
  status: PickedFileStatus,
  error: string | null
): IPickedFilesState => ({
  ...state,
  files: state.files.map((f) => (f.key === key ? { ...f, status, error } : f)),
});

export const pickedFilesReducer = (
  state: IPickedFilesState,
  action: PickedFilesAction
): IPickedFilesState => {
  switch (action.type) {
    case "add": {
      const allowed = action.files.filter((f) => isAllowedUpload(f.name));
      return {
        files: [
          ...state.files,
          ...allowed.map((file, i) => ({
            key: `file-${state.nextId + i}`,
            file,
            status: "queued" as const,
            error: null,
          })),
        ],
        rejected: action.files
          .filter((f) => !isAllowedUpload(f.name))
          .map((f) => f.name),
        nextId: state.nextId + allowed.length,
      };
    }
    case "remove":
      return {
        ...state,
        files: state.files.filter(
          (f) => f.key !== action.key || !isRemovable(f)
        ),
      };
    case "uploading":
      return setStatus(state, action.key, "uploading", null);
    case "done":
      return setStatus(state, action.key, "done", null);
    case "failed":
      return setStatus(state, action.key, "failed", action.error);
    case "reset":
      return INITIAL_PICKED_FILES;
    default:
      return state;
  }
};

export interface IUploadProgress {
  done: number;
  failed: number;
  total: number;
  /** Whether any file has started uploading — before that, the count means nothing. */
  started: boolean;
}

export const uploadProgress = (files: IPickedFile[]): IUploadProgress => ({
  done: files.filter((f) => f.status === "done").length,
  failed: files.filter((f) => f.status === "failed").length,
  total: files.length,
  started: files.some((f) => f.status !== "queued"),
});

export interface INewModelActions {
  /** "Start modeling" — also what continues after a generate that failed, or once the failures are gone. */
  start: boolean;
  /** "Retry failed". */
  retryFailed: boolean;
  /** "Start with N uploaded": N, or null when not shown. */
  startWithUploaded: number | null;
  /** "Open without starting": the model exists, so she can always go to it and finish there. */
  openWithoutStarting: boolean;
}

/**
 * The dialog's footer. Before the model exists, just "Start modeling". Once it exists and a file failed:
 * "Retry failed", "Start with N uploaded" (only when at least one is up — generating with none is a 400)
 * and "Open without starting" in its place. Once nothing is failing, "Start modeling" is back.
 */
export const newModelActions = (
  runId: string | null,
  files: IPickedFile[]
): INewModelActions => {
  const { done, failed } = uploadProgress(files);
  const partial = !!runId && failed > 0;
  return {
    start: !partial,
    retryFailed: partial,
    startWithUploaded: partial && done > 0 ? done : null,
    openWithoutStarting: !!runId,
  };
};
